import { useEffect, useRef, useState } from 'react'
import {
  CSV_COLUMN_INFO,
  CSV_TEMPLATE_HEADERS,
  EXAMPLE_EMPLOYEES,
  PLAN_LIMITS,
  SELECTABLE_PLAN_YEARS,
} from '@/lib/constants'
import { CSV_TEMPLATE_EXAMPLE_COUNT } from '@/lib/csv'
import { formatCurrency } from '@/lib/utils'

// One scrollable reference document, with a sticky table of contents that
// jumps to each section. Sections mirror the workspace and the calculation
// engine in src/lib/calculations.js so the methodology stays accurate.

const SECTIONS = [
  { id: 'overview', title: 'Overview' },
  { id: 'workspace', title: 'Calculator steps' },
  { id: 'census', title: 'Employee data' },
  { id: 'csv', title: 'CSV column reference' },
  { id: 'importstudio', title: 'The import review' },
  { id: 'estimate', title: 'Estimate Mode' },
  { id: 'plan', title: 'Plan configuration' },
  { id: 'cashbalance', title: 'Cash-balance illustration' },
  { id: 'presets', title: 'Plan presets' },
  { id: 'outputs', title: 'Outputs & charts' },
  { id: 'compare', title: 'Compare mode' },
  { id: 'exports', title: 'Exports' },
  { id: 'clientbook', title: 'Clients & saving' },
  { id: 'assistant', title: 'Julio & AI features' },
  { id: 'methodology', title: 'Calculation methodology' },
  { id: 'limits', title: 'Contribution limits' },
  { id: 'privacy', title: 'Data & privacy' },
]

function H({ children }) {
  return <h3 className="mt-1 text-lg font-semibold tracking-tight text-foreground">{children}</h3>
}

function Sub({ children }) {
  return <h4 className="mt-5 text-sm font-semibold text-foreground">{children}</h4>
}

function P({ children }) {
  return <p className="mt-2.5 text-sm leading-relaxed text-muted-foreground">{children}</p>
}

function List({ children }) {
  return <ul className="mt-2.5 ml-1 space-y-1.5 text-sm leading-relaxed text-muted-foreground">{children}</ul>
}

function LI({ children }) {
  return (
    <li className="flex gap-2">
      <span className="mt-2 h-1 w-1 shrink-0 rounded-full bg-muted-foreground/50" />
      <span>{children}</span>
    </li>
  )
}

function Term({ children }) {
  return <span className="font-medium text-foreground">{children}</span>
}

function Note({ title = 'Compliance note', children }) {
  return (
    <div className="mt-3 rounded-md border border-amber-200 bg-amber-50 px-3 py-2.5 text-sm leading-relaxed text-amber-900">
      <span className="font-semibold">{title}: </span>
      {children}
    </div>
  )
}

function Section({ id, title, children }) {
  return (
    <section id={id} className="scroll-mt-4 border-t border-border pt-8 first:border-t-0 first:pt-0">
      <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{title}</p>
      {children}
    </section>
  )
}

// Horizontal overflow here needs an unbroken chain of min-w-0. This renders as
// a grid item of DialogContent, and grid/flex items default to min-width:auto —
// they refuse to shrink below their widest child. The wide tables below would
// otherwise push the root past the dialog's width, where the dialog's
// overflow-hidden just clips them, and every overflow-x-auto inside is dead.
// The chain is: root → the flex row → the content pane → each table's scroller.
export default function AppDocs() {
  const contentRef = useRef(null)
  const [activeId, setActiveId] = useState(SECTIONS[0].id)

  const goToSection = (id) => {
    setActiveId(id)
    const container = contentRef.current
    const target = container?.querySelector(`#${id}`)
    if (target && container) {
      container.scrollTo({ top: target.offsetTop - 8, behavior: 'smooth' })
    }
  }

  // Keep the active indicator in sync with scroll position: the active section
  // is the last one whose top has passed the container's top edge.
  useEffect(() => {
    const container = contentRef.current
    if (!container) return

    let frame = null
    const updateActiveSection = () => {
      frame = null
      const sections = SECTIONS
        .map((section) => container.querySelector(`#${section.id}`))
        .filter(Boolean)
      if (sections.length === 0) return

      const containerTop = container.getBoundingClientRect().top
      const activeLine = 12

      let nextActive = sections[0].id
      if (container.scrollTop + container.clientHeight >= container.scrollHeight - 2) {
        // Scrolled to the bottom: the final section can't reach the top edge,
        // so treat it as active.
        nextActive = sections[sections.length - 1].id
      } else {
        for (const section of sections) {
          if (section.getBoundingClientRect().top - containerTop <= activeLine) {
            nextActive = section.id
          } else {
            break
          }
        }
      }

      setActiveId(nextActive)
    }

    const handleScroll = () => {
      if (frame) return
      frame = requestAnimationFrame(updateActiveSection)
    }

    updateActiveSection()
    container.addEventListener('scroll', handleScroll, { passive: true })
    window.addEventListener('resize', handleScroll)
    return () => {
      container.removeEventListener('scroll', handleScroll)
      window.removeEventListener('resize', handleScroll)
      if (frame) cancelAnimationFrame(frame)
    }
  }, [])

  return (
    <div className="flex h-[calc(86dvh-2.5rem)] w-full min-w-0 flex-col gap-4 sm:h-[calc(86dvh-3rem)]">
      <div className="shrink-0">
        <h2 className="text-xl font-semibold tracking-tight">Documentation</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          Full feature guides and the calculation methodology behind every result.
        </p>
      </div>

      {/* Below `sm` the sidebar has nowhere to go, so the same sections become a
          horizontally scrollable strip rather than disappearing and leaving the
          docs with no navigation at all. Same scroll affordance as the workspace
          section nav. */}
      <nav aria-label="Documentation sections" className="section-nav-scroll -mx-1 flex shrink-0 items-center gap-1.5 overflow-x-auto px-1 pb-1 sm:hidden">
        {SECTIONS.map((section) => (
          <button
            key={section.id}
            type="button"
            onClick={() => goToSection(section.id)}
            aria-current={activeId === section.id ? 'true' : undefined}
            className={[
              'shrink-0 whitespace-nowrap rounded-full border px-3 py-1 text-xs transition-colors',
              activeId === section.id
                ? 'border-primary bg-primary/10 font-medium text-foreground'
                : 'border-input text-muted-foreground',
            ].join(' ')}
          >
            {section.title}
          </button>
        ))}
      </nav>

      {/* min-w-0 on both the row and the content pane is load-bearing: a flex
          item defaults to min-width:auto, so it refuses to shrink below its
          widest child. Without it the tables below push this pane wider than the
          screen and their own overflow-x-auto never gets a chance to scroll —
          the whole panel overflows instead. */}
      <div className="flex min-h-0 min-w-0 flex-1 gap-5">
        <nav aria-label="Documentation sections" className="hidden w-52 shrink-0 overflow-y-auto border-r border-border pr-3 sm:block">
          <ul className="space-y-0.5">
            {SECTIONS.map((section) => (
              <li key={section.id}>
                <button
                  type="button"
                  onClick={() => goToSection(section.id)}
                  aria-current={activeId === section.id ? 'true' : undefined}
                  className={[
                    'w-full rounded px-2 py-1.5 text-left text-sm transition-colors',
                    activeId === section.id
                      ? 'bg-muted font-medium text-foreground'
                      : 'text-muted-foreground hover:bg-muted/50 hover:text-foreground',
                  ].join(' ')}
                >
                  {section.title}
                </button>
              </li>
            ))}
          </ul>
        </nav>

        <div ref={contentRef} className="min-h-0 min-w-0 flex-1 space-y-8 overflow-y-auto rounded-lg bg-card/60 p-4 sm:p-6">
          <Section id="overview" title="Overview">
            <H>What this tool does</H>
            <P>
              This calculator models employer and employee contributions to a 401(k) plan for a single plan
              year. You provide employee data and plan settings; it computes each person’s deferrals,
              employer contributions, vesting, and eligibility, then rolls those up into plan-level metrics and
              charts.
            </P>
            <P>
              Everything runs locally in your browser. Results are estimates for planning and illustration – they
              are not a compliance test, an actuarial valuation, or tax advice. Always confirm plan design
              decisions with your recordkeeper, TPA, or ERISA counsel.
            </P>
            <Sub>The basic workflow</Sub>
            <List>
              <LI><Term>1 Employees:</Term> add, upload, or try a sample employee list.</LI>
              <LI><Term>2 Plan Settings:</Term> choose how the plan works.</LI>
              <LI><Term>3 Checks:</Term> review missing information and modeled issues.</LI>
              <LI><Term>4 Results:</Term> review totals, employee results, and charts.</LI>
              <LI><Term>5 Reports:</Term> preview and export a PDF, interactive report, or data file.</LI>
            </List>
          </Section>

          <Section id="workspace" title="Calculator steps">
            <H>Moving through the calculator</H>
            <P>
              Use the five numbered steps across the top. Completed steps stay available, so you can go back and
              change an employee or plan choice without starting over.
            </P>
            <List>
              <LI><Term>Start here</Term> opens the guided setup for a new client.</LI>
              <LI><Term>Plan menu</Term> keeps naming, duplication, client management, templates, import, and backup actions together.</LI>
              <LI><Term>Compare</Term> adds or hides options shown beside the Current Plan.</LI>
              <LI>The header arrows undo and redo recent edits.</LI>
            </List>
          </Section>

          <Section id="census" title="Employee data">
            <H>Creating and managing the employee list</H>
            <P>The Employees step is where you create, replace, download, or clear the employee list.</P>
            <P>
              The public demo includes a full {EXAMPLE_EMPLOYEES.length}-employee sample. You can edit, add, or delete
              employees in that sample, or import your own employee file, including the populated CSV template.
            </P>
            <List>
              <LI><Term>Load example data</Term> replaces the employee list with a built-in sample so you can explore populated controls.</LI>
              <LI><Term>Download CSV template</Term> gives you one populated file with {CSV_TEMPLATE_EXAMPLE_COUNT} deliberately varied employees covering common inputs and optional blanks.</LI>
              <LI><Term>Upload</Term> a completed CSV from the dashed drop area, or drag it there. Importing replaces the entire current employee list after it is parsed and validated.</LI>
              <LI><Term>Add employee</Term> creates one editable row. <Term>Clear all employees</Term> removes the employee list after a confirmation.</LI>
              <LI>Messier real-world files – Excel workbooks, several files at once, odd headers, or plan documents – open the <Term>import review</Term> (see its section below), which shows exactly what will import before anything changes.</LI>
            </List>
            <Sub>Editing in the table</Sub>
            <List>
              <LI>Edit a cell directly; the change applies when you leave the field.</LI>
              <LI><Term>Status</Term> controls termination treatment, a dollar deferral overrides its percentage, and a valid <Term>tenure</Term> entry overrides the hire date for service.</LI>
              <LI>Click a heading to cycle ascending → descending → unsorted. <Term>Search</Term>, <Term>Status</Term>, and <Term>Gender</Term> narrow the table; <Term>Clear</Term> removes the search and filters.</LI>
              <LI>Row checkboxes select employees; the header checkbox selects every matching row across pages. <Term>Delete selected</Term> removes only checked rows.</LI>
              <LI>For larger matching sets, use Previous and Next to page in the expanded editor. Each heading’s question mark shows that field’s definition.</LI>
            </List>
            <P>Only <Term>DOB</Term> and <Term>compensation</Term> are required. Anything missing is treated as unknown rather than zero, and the affected result is flagged instead of silently guessed.</P>
          </Section>

          <Section id="csv" title="CSV column reference">
            <H>Supported columns</H>
            <P>
              Headers are matched by these exact names. Order does not matter, and optional columns may be left
              blank or omitted.
            </P>
            {/* overflow-hidden on its own clips the right-hand columns on a
                narrow screen with no way to reach them; the inner scroller keeps
                the rounded border while letting the table pan. */}
            <div className="mt-4 overflow-hidden rounded-lg border border-border">
              <div className="overflow-x-auto">
              <table className="w-full min-w-[34rem] border-collapse text-left text-sm">
                <thead>
                  <tr className="bg-muted/60 text-xs uppercase tracking-wide text-muted-foreground">
                    <th className="px-3 py-2 font-semibold">Column</th>
                    <th className="px-3 py-2 font-semibold">Req.</th>
                    <th className="px-3 py-2 font-semibold">Meaning</th>
                    <th className="px-3 py-2 font-semibold">Example</th>
                  </tr>
                </thead>
                <tbody>
                  {CSV_TEMPLATE_HEADERS.map((header) => {
                    const info = CSV_COLUMN_INFO[header]
                    if (!info) return null
                    return (
                      <tr key={header} className="border-t border-border align-top">
                        <td className="px-3 py-2 font-mono text-xs text-foreground">{header}</td>
                        <td className="px-3 py-2 text-xs text-muted-foreground">{info.required ? 'Yes' : 'No'}</td>
                        <td className="px-3 py-2 text-xs leading-relaxed text-muted-foreground">{info.description}</td>
                        <td className="px-3 py-2 font-mono text-xs text-muted-foreground">{info.example}</td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
              </div>
            </div>
          </Section>

          <Section id="importstudio" title="The import wizard">
            <H>Three steps: File → Columns → Review</H>
            <P>
              Every employee file comes in through one door: the dashed drop area on Employees. A clean
              template CSV imports directly after a confirmation. Anything messier – Excel workbooks, odd headers,
              several files at once – opens the import wizard (a signed-in feature) with one decision per step.
              Nothing changes until the final Import button, and an employee file and an SPD uploaded together never lose
              each other&rsquo;s work.
            </P>
            <Sub>1 · File</Sub>
            <List>
              <LI>Shown only when there is something to decide: which worksheet of an Excel workbook to use, or which files are part of the import. One file with one sheet skips straight to Columns.</LI>
            </List>
            <Sub>2 · Columns</Sub>
            <List>
              <LI>Your file is shown as a small table with its real headers and first few rows. A <Term>becomes</Term> row shows which employee field each column maps to. Matched columns are filled in; amber columns need your review.</LI>
              <LI>When a column&rsquo;s header cell was blank, any label stranded in a title row above the headers (TPA sheets often park names like &ldquo;SH 3%NEC&rdquo; or &ldquo;ER P/S&rdquo; there) is salvaged and used as the column&rsquo;s title, so you can tell what it was.</LI>
              <LI>Empty columns, 1-2-3 counter columns, and middle initials are set aside automatically behind a <Term>+N set aside</Term> stub at the table&rsquo;s right edge – expanding it appends them there, dimmed, without reshuffling the columns you were looking at.</LI>
              <LI>Split <Term>Last / First</Term> name columns combine into one Name. An hours column that also carries <Term>FT / PT</Term> markers fills two employee fields at once – Hours Served from the numbers and a <Term>+ Full Time</Term> tag from the markers, removable in one click. Value cleanups run automatically, and the Review step shows their result.</LI>
            </List>
            <Sub>3 · Review</Sub>
            <List>
              <LI>The final employee list appears exactly as it will import, with cleaned values. Cells that block a row are tinted red; <Term>Only rows with problems</Term> filters to them, and any cell can be edited in place.</LI>
              <LI>With multiple files, this step also combines them: <Term>stack</Term> different employees or <Term>match</Term> the same employees across files, with disagreements highlighted amber and a pick-per-cell (or trust one file for everything).</LI>
            </List>
            <Sub>Match with AI</Sub>
            <P>
              For confusing headers (“Assoc #”, “Ann Base Rt”) or unusual value formats, <Term>Match with AI</Term>
              asks the AI to propose column matches and cleanups. It sees only column headers, masked value shapes
              (every digit becomes a 9 and every letter an X, so a date looks like 99/99/9999), and repeating
              category labels like FT/PT – never your employee rows. Exact header matches are never overridden, and
              every suggestion lands in the same reviewable list for you to confirm.
            </P>
            <Sub>Plan documents</Sub>
            <P>
              Drop an SPD, adoption agreement, or plan-highlights sheet (PDF or text; scanned PDFs are OCR&rsquo;d in
              your browser). The document&rsquo;s text is read by the AI and mapped onto the plan settings this app
              models; each extracted setting appears as a before → after row with its own checkbox and applies to
              the plan you choose only when you confirm. Anything the app can&rsquo;t model – an unsupported plan
              year, an implausible value – is listed as ignored with a plain-English reason.
            </P>
          </Section>

          <Section id="estimate" title="Estimate Mode">
            <H>Model a plan with no employee file</H>
            <P>
              Estimate Mode (signed-in) is built for prospecting and first meetings: enter the rough numbers a
              client can tell you from memory – headcount, typical pay, owners, participation, part-time share,
              turnover – and the app generates a realistic sample employee list that every feature runs on. The
              calculator is clearly marked as an estimate while it is active, and it stays separate from real client
              data: your real employees and plans come back untouched when you exit.
            </P>
            <List>
              <LI>Optionally describe the company in a sentence and let the AI prefill the form; every assumption stays visible and editable before the estimate starts.</LI>
              <LI>Edit the assumptions any time from the estimate banner; the sample employees update from them.</LI>
              <LI>Saving a client while estimating stores the assumptions only (never the synthetic rows), so loading it later rebuilds the same estimate.</LI>
            </List>
          </Section>

          <Section id="plan" title="Plan configuration">
            <H>Plan rules</H>
            <P>Plan settings are grouped by purpose. They change the calculation, never the employee data.</P>
            <Sub>Employer contributions</Sub>
            <List>
              <LI><Term>Tiered match:</Term> match a percentage of pay across one or more tiers (e.g. 100% up to 1%, then 50% up to 6%). An optional per-employee dollar cap limits total match.</LI>
              <LI><Term>NEC (nonelective contribution):</Term> a fixed percentage of compensation regardless of deferrals. A safe-harbor NEC is always immediately vested.</LI>
              <LI><Term>Profit sharing:</Term> one guided designer with three decisions. <Term>Formula</Term> chooses pro rata, flat dollar, integrated, points, age-weighted, or new comparability. <Term>Funding</Term> uses the specified formula, a dollar budget when compatible, or Maximize owners when supported. <Term>Conditions</Term> sets minimum hours and last-day employment with fixed AND semantics.</LI>
            </List>
            <Sub>Allocation classes and new comparability</Sub>
            <List>
              <LI>An <Term>allocation class</Term> is a code assigned to an employee, such as Owner or Staff. Assign classes on the <Term>Employees</Term> step. Class-based match, new comparability, and structured cash balance use those same classes.</LI>
              <LI>New comparability shows one allocation rate per class, live participant counts, and live gateway, HCE rate-group, and ABPT results. <Term>Maximize owners</Term> opens the guided designer, which steps through readiness (blockers vs. review items), a goal (maximize owners, target contributions, or a budget), deterministic scenario cards, and every modeled screen (gateway with both measurement paths, coverage, rate groups, ABPT, §415(c), a §404 deduction screen, and top-heavy) before Apply.</LI>
              <LI>Every result carries a cautious status: <Term>no modeled flag</Term>, <Term>flagged for review</Term>, <Term>professional review needed</Term>, <Term>unavailable</Term>, or <Term>not applicable</Term>. A flagged or unavailable design cannot be applied; a review-needed design requires a recorded acknowledgment, and applying stores the proposal (goal, scenario, assumptions, acknowledgment) with the plan. TPA worksheets export as CSV or a JSON calculation package.</LI>
              <LI><Term>Combined DB/DC designs:</Term> when the plan has an enabled cash-balance component, the designer switches to the combined path (26 CFR 1.401(a)(4)-9): the minimum AGGREGATE allocation gateway (one-third / 5% / sliding above 25% / 7.5% deemed), the elected NHCE cash-balance rate averaging, §401(a)(26) minimum participation, aggregate normal and most-valuable (QJSA-route) rate groups, §415(b) owner credit solves on versioned proposal-verified annuity tables, and the §404(a)(7) combined-deduction screen. Aggregation is never automatic (you confirm it), a DC-only result cannot clear the modeled combined-plan screens while cash balance is on, and every combined result remains flagged for professional review until the plan&rsquo;s enrolled actuary confirms the DB conversion assumptions.</LI>
              <LI>Employee assignments, per-source overrides, solver targets, and per-person amounts stay in this browser. They are never included in saved plan settings, Client plans, exports, analytics, or AI payloads.</LI>
            </List>
            <Sub>After-tax (mega-backdoor Roth)</Sub>
            <List>
              <LI><Term>Allow after-tax contributions:</Term> enables employee after-tax (non-Roth) contributions that fill the 415(c) cap above deferrals and employer money. When off, after-tax amounts in the employee list are ignored with a warning.</LI>
              <LI><Term>Max after-tax (% of comp / $):</Term> optional plan caps applied before the 415(c) limit. Leave a cap at 0 for no plan-specific limit.</LI>
            </List>
            <Note>
              These toggles model plan design only – they do not verify that the plan document permits after-tax
              contributions or in-plan Roth conversions, or that the recordkeeper supports them. After-tax
              contributions remain ACP-tested (§401(m)) even in a safe harbor plan, and this tool does not run that test.
            </Note>
            <Sub>Minimum eligibility</Sub>
            <P>
              One eligibility result – based on age, months of service, and plan-year hours – gates the
              <Term> whole plan</Term>, the common plan design: an employee who fails a requirement is not yet a
              participant and makes no contributions at all – no employee deferrals, no after-tax, and no employer
              money. When a required value is missing, eligibility is reported as <Term>unknown</Term> and the
              employee is modeled as a full participant (with a warning) rather than zeroed out.
            </P>
            <Sub>Long-term part-time (LTPT) deferrals</Sub>
            <P>
              The one statutory exception: under SECURE 2.0, from the 2025 plan year employees with 500+ hours in
              each of two consecutive years (age 21+) must be allowed to make employee deferrals even when they miss
              the requirements above. Plans may still exclude them from employer contributions and testing, which is
              how this models them. Because the employee list holds one year of hours, an employee counts as LTPT when they
              meet the age rule, are not marked <Term>full-time</Term>, and have 500+ hours with 2+ years of service
              – a stand-in for the statute&rsquo;s two-consecutive-500-hour-years test. The app applies this rule for
              2025+ plan years rather than treating it as a plan-design toggle.
            </P>
            <Sub>Vesting</Sub>
            <P>
              Each employer source can be immediate, cliff, or graded. When a schedule depends on service and
              service is unknown, that source’s vesting is reported as <Term>unresolved</Term> instead of being
              treated as 0%.
            </P>
            <Sub>Termination handling</Sub>
            <List>
              <LI><Term>Include terminated employees in plan metrics</Term> adds them to participation rates, averages, and charts (off by default).</LI>
              <LI><Term>Terminated employees earn employer contributions</Term> decides whether records marked Terminated receive employer money under the eligibility result.</LI>
              <LI><Term>Prorate compensation:</Term> ON expects an annualized salary that is scaled for an in-year hire or termination; OFF expects actual plan-year or YTD compensation with no scaling.</LI>
              <LI><Term>Short-term termination threshold</Term> flags terminations within N days of hire in turnover reporting.</LI>
            </List>
            <Sub>Plan year & limits</Sub>
            <P>
              Selecting the plan year sets the IRS limits used everywhere – the deferral limit, catch-up amounts,
              the 415(c) annual-additions cap, the 401(a)(17) compensation cap, and the HCE threshold. See
              <Term> Contribution limits</Term> for the current values.
            </P>
          </Section>

          <Section id="cashbalance" title="Cash-balance illustration">
            <H>Cash-balance plans: what retira.app models</H>
            <P>
              The cash-balance card models explicit annual hypothetical benefit credits by allocation group alongside
              the 401(k) design for the 2026 plan year. It is a planning illustration, not an actuarial valuation,
              funding engine, or formal defined-benefit compliance test.
            </P>
            <Sub>Explicit credits and scenario solves</Sub>
            <List>
              <LI><Term>Benefit credits</Term> assigns each group either an explicit percent of pay or a flat dollar amount. A group with no adopted credit receives no assumed amount.</LI>
              <LI><Term>Fixed interest credit</Term> drives the directional balance projection. The illustration uses a fixed 3-year cliff vesting assumption.</LI>
              <LI><Term>Maximize owners</Term> uses the same guided designer described above. With cash balance enabled it solves a scenario-grade §415(b)-limited owner credit, the aggregate gateway, §401(a)(26), normal and most-valuable rate groups, and the combined deduction screen from disclosed proposal-derived actuarial assumptions.</LI>
            </List>
            <Sub>Results schedule</Sub>
            <P>
              Results show a directional 10-year schedule that repeats the selected year-one credits and applies the
              fixed interest-credit rate. It is not an actuarial funding schedule.
            </P>
            <Sub>Read the results in two separate columns</Sub>
            <P>
              Defined-contribution employer contributions remain a DC amount. Cash-balance hypothetical benefit credits
              appear separately as defined-benefit illustrations. When a proposal summarizes both, the combined figure is
              labeled modeled employer allocation – never required funding, deductible cost, or a formal quote.
            </P>
            <Note title="Actuary review required">
              The combined solve is a planning scenario, not an actuarial certification. retira.app does not determine
              required funding or replace formal defined-benefit testing. Confirm the actuarial assumptions, adopted
              benefit formula, funding corridor, and formal results with the plan’s TPA or enrolled actuary.
            </Note>
          </Section>

          <Section id="presets" title="Plan presets">
            <H>Starting from a preset</H>
            <P>
              The sparkles button in each plan column applies a ready-made design to that plan. In the single-plan
              view it applies to the Current Plan. Presets change plan rules only – never employees or calculated results.
              Available presets include safe harbor nonelective, QACA and traditional matches, new comparability,
              and cash balance combinations. Apply one as a starting point,
              then adjust individual settings.
            </P>
            <Sub>Reuse your own designs</Sub>
            <P>
              Open the <Term>Plan menu</Term>, choose <Term>Plan templates</Term>, and save an option. It can then be
              applied to any option without rebuilding it. Applying a plan template
              replaces that option&rsquo;s rules, preserves its display name, and can be undone. Plan templates contain
              plan settings only, stay in this browser, and never include employee data.
            </P>
          </Section>

          <Section id="outputs" title="Outputs & charts">
            <H>Reading the results</H>
            <List>
              <LI><Term>Summary</Term> shows eligibility counts, known and unresolved vesting, top-heavy availability, participation, and contribution totals.</LI>
              <LI><Term>Employee details</Term> breaks down each person’s deferrals, employer contributions by source, eligibility, vesting, warnings, and estimated forfeitures. Its search and filters also scope the results CSV export.</LI>
              <LI><Term>Testing</Term> keeps the gateway, HCE rate groups, ABPT, coverage, top-heavy, and ADP/ACP signals together with assumptions and review findings. These are modeled screens, not formal test results.</LI>
              <LI><Term>Profit-sharing allocation comparison</Term> appears in the single-plan view and shows each employee&rsquo;s pro-rata allocation beside the Current Plan, with the dollar and percentage difference.</LI>
              <LI><Term>Cash-balance schedule</Term> shows the directional projection for a structured design. It does not represent required funding or a maximum solve.</LI>
            </List>
            <Sub>Interactive charts</Sub>
            <List>
              <LI>Click a chart bar, point, pie segment, legend item, or summary group to open the employees behind it.</LI>
              <LI>Drag across bars or scatter points to open one combined group. Press Escape to cancel a drag.</LI>
              <LI>On touch screens, tap the chart’s pointer button before dragging. The group dialog can reveal identifiers or export that selection to CSV.</LI>
            </List>
            <P>
              By default, charts and aggregate metrics describe active employees only; terminated employees are
              included only when the termination setting turns them on.
            </P>
          </Section>

          <Section id="compare" title="Compare plans">
            <H>Comparing plan designs side by side</H>
            <P>
              The Current Plan is your starting point. Add up to three alternatives, called Option 2 through Option 4,
              against the same employee list. Each option starts as a copy of the Current Plan.
            </P>
            <List>
              <LI><Term>Manage options from Compare in the header.</Term> It is the single place to add, rename, show, hide, or delete an option.</LI>
              <LI><Term>Shown plans stay consistent.</Term> The same set appears throughout Plan Settings, Checks, Results, and Reports. Hiding a plan keeps it saved but removes it from those comparisons and from client-facing reports.</LI>
              <LI><Term>Every shown option appears at once.</Term> The Current Plan stays visible as the starting point. The single-plan screens return when every alternative is hidden.</LI>
              <LI><Term>Delete removes an alternative.</Term> Use Undo in the header if you need to restore it.</LI>
              <LI><Term>The Results summary shows every shown option at once</Term>, with the change from the Current Plan under each number.</LI>
              <LI><Term>Restore from Current Plan</Term> discards an option&rsquo;s edits and copies the Current Plan again.</LI>
            </List>
          </Section>

          <Section id="exports" title="Exports">
            <H>Getting data out</H>
            <List>
              <LI><Term>Report Studio</Term> keeps the generated document visible while you edit its cover, branding, assumptions, and contents. The live preview refreshes shortly after you stop typing.</LI>
              <LI><Term>PDF</Term> produces Current Plan detail plus a side-by-side section for every shown alternative. When alternatives are shown, Reports offers only comparison-capable formats; hide them to use the single-plan one-page, spreadsheet, and CSV exports.</LI>
              <LI><Term>Interactive report</Term> produces a single .html file with interactive charts (hover for values, drag across charts to open employee groups). Cover details, logo, and section choices are edited beside the preview.</LI>
              <LI>
                The complete PDF and interactive report are built from the same major parts:{' '}
                <Term>What it costs and who gets it</Term> (the cost summary, the options side by side, and what each
                employee gets), <Term>Plan details and assumptions</Term>, and <Term>Workforce charts</Term>. Modeled
                compliance screens remain inside the Checks stage and are not included in client-facing reports. Open
                a part in the settings rail to include or drop its individual pages, blocks, and charts.
              </LI>
              <LI>
                The workforce charts ship <Term>off</Term>: they are a workforce study rather than part of a plan
                proposal, so the default report is the short client-facing one. One checkbox adds them back.
              </LI>
              <LI><Term>Export results CSV</Term> contains the calculated employee rows matching the Employee details search and filters. A chart-group CSV contains only that selected group.</LI>
              <LI><Term>Export employee list CSV</Term> contains the editable employee inputs matching the Employees table&rsquo;s current search and filters.</LI>
              <LI><Term>Input CSV template</Term> contains the supported headers and {CSV_TEMPLATE_EXAMPLE_COUNT} varied example employees, so it can be edited and loaded directly in the demo.</LI>
              <LI><Term>Download plan backup</Term> in the Plan menu creates reusable JSON containing plan assumptions only – never employees or results.</LI>
            </List>
          </Section>

          <Section id="clientbook" title="Clients & saving">
            <H>Switching clients and automatic saving</H>
            <P>
              Client plans save a client label and the current
              plan settings, including compared options. Employee data is never uploaded. The control is visible but
              locked in the public demo; log in to use it.
            </P>
            <Note title="Privacy note">
              Employee data is never uploaded. Client plans syncs only the client label and plan settings
              to your account. Selecting an entry applies those choices to the employee list open in this browser;
              it never downloads or replaces employees. See <Term>Privacy &amp; Terms</Term> for full details.
            </Note>
            <Sub>Saving and switching clients</Sub>
            <List>
              <LI>Plan changes save automatically. The compact header status shows when the plan is saving, saved, available only on this device, or needs a retry.</LI>
              <LI><Term>Untitled client</Term> is the account draft. Name it when you want it in the client list.</LI>
              <LI><Term>Duplicate client</Term> creates a new named copy without changing the original.</LI>
              <LI>Open <Term>Manage clients</Term>, then select a client row to apply its saved plan settings. Employee data remains unchanged.</LI>
              <LI>Use the three-dot menu on a client row only for renaming or deleting its saved label and plan settings.</LI>
            </List>
            <P>
              The browser also keeps a local copy. If the network drops, edits remain on this device and sync resumes
              when the connection returns.
            </P>
          </Section>

          <Section id="assistant" title="Julio & AI features">
            <H>The AI assistant</H>
            <P>
              <Term>Julio</Term>, the calculator assistant, answers questions about the
              loaded employees and plan, surfaces insights, builds charts, and can
              propose plan-setting changes. A proposed change is staged on a card showing before → after values
              computed from the real plan objects – nothing applies until you click Apply, and only recognized plan
              settings can ever be changed; employee data has no path in.
            </P>
            <Sub>Goal-seek optimizer</Sub>
            <P>
              Ask Julio for a design goal rather than a specific setting – “what’s the least I can pay?”, “cheapest
              way to pass the ADP screen?”, “most NHCE benefit per dollar?”, or “get the most into the owner’s
              account / an owner-heavy design”. A deterministic engine searches a bounded grid of designs (including
              owner-heavy new-comparability class allocations) against the same compliance screens the calculator
              uses – ADP/ACP, §410(b) coverage, and the modeled §401(a)(4) cross-testing – then stages the winning
              design into the requested plan and the runner-ups into the other compare slots, so you can open Compare
              and read them side by side. Each staged design is a modeled result under those screens, never a legal
              minimum, and cross-tested designs use documented actuarial assumptions rather than the plan’s actual
              elections – confirm with the plan’s TPA or actuary.
            </P>
            <Sub>Julio&rsquo;s privacy pipeline</Sub>
            <List>
              <LI>Built-in tools analyze your employee and plan data locally, in the browser.</LI>
              <LI>A privacy filter strips anything identifying before a single byte leaves the page – only aggregates, redacted findings, and pseudonymous employee tokens reach the hosted AI.</LI>
              <LI>Names and identifiers are re-attached locally, in your browser, only as the answer renders.</LI>
            </List>
            <Sub>One-time consent, per feature</Sub>
            <P>
              Every AI feature shows a dialog the first time you use it, listing exactly what is and is not sent:
              <Term> Julio</Term> (sanitized aggregates only), <Term>plan-document import</Term> (the
              document&rsquo;s text, never employee data), the <Term>estimator&rsquo;s form-filler</Term> (only the
              description you typed), and the Studio&rsquo;s <Term>Match with AI</Term> (column headers and masked
              value shapes). Data travels over an encrypted connection under a Zero Data Retention policy – nothing
              is stored or used for training – and each choice is remembered on the device.
            </P>
            <Note title="Advice note">
              Julio&rsquo;s answers are general information, not legal, tax, or investment advice. Chats aren&rsquo;t
              saved by retira.app.
            </Note>
          </Section>

          <Section id="methodology" title="Calculation methodology">
            <H>How each employee is calculated</H>
            <P>
              Every employee is computed independently for the selected plan year, following the steps below. All
              dollar limits come from that year’s IRS figures.
            </P>

            <Sub>1. Valuation date</Sub>
            <P>
              Age, service, and vesting are measured as of a valuation date. For active employees this is the last
              day of the plan year. For a record marked <Term>Terminated</Term> with a usable in-year termination
              date, the valuation date is that termination date. If a terminated record’s date is missing, invalid,
              before the hire date, or after the plan year, the plan-year end is used instead and a warning is
              added. A termination date on a non-terminated record is ignored.
            </P>

            <Sub>2. Compensation, proration & the comp cap</Sub>
            <List>
              <LI>When proration is <Term>off</Term>, compensation is used as entered.</LI>
              <LI>When proration is <Term>on</Term> and the employee is hired or terminated during the plan year, compensation is scaled by the fraction of the year worked, counted in days (leap years use 366).</LI>
              <LI>Compensation is then capped at the 401(a)(17) limit. This <Term>capped compensation</Term> is the basis for all percentage-based contributions.</LI>
            </List>

            <Sub>3. Service & tenure</Sub>
            <P>
              Service is the time from hire date to the valuation date, expressed in months. A valid <Term>tenure</Term>
              entry overrides the hire date (use text like “5y 3m” for years and months). <Term>Service years</Term>
              for vesting is the whole number of completed years. If
              neither a hire date nor a valid tenure is available and service is needed, the affected eligibility
              and vesting results are marked unknown rather than assumed.
            </P>

            <Sub>4. Plan eligibility & LTPT</Sub>
            <P>
              If minimum eligibility is enabled, the employee’s age, service months, and hours are each checked
              against the minimums <Term>before</Term> any contribution is computed. A requirement with no minimum
              set is not applicable; a missing value is unknown.
            </P>
            <List>
              <LI>Any <Term>failed</Term> requirement makes the employee ineligible: requested employee deferrals and after-tax contributions are not modeled (the request stays visible), and employer contributions are zero.</LI>
              <LI><Term>LTPT carve-out (SECURE 2.0):</Term> an employee who meets the age rule, is not marked full-time, and has 500+ plan-year hours with 24+ months of service may make employee deferrals while remaining excluded from employer contributions and the informal testing screens.</LI>
              <LI>If nothing fails but something is <Term>unknown</Term>, eligibility is unknown and the employee is modeled as a full participant (with a warning).</LI>
              <LI>A terminated employee earns no employer money if the plan setting says so; deferrals made while employed still count.</LI>
            </List>

            <Sub>5. Employee deferrals & IRS limits</Sub>
            <List>
              <LI>Deferrals are modeled only for employees eligible to participate (including LTPT); an ineligible employee’s requested deferral is reported but never counted.</LI>
              <LI>Pre-tax and Roth deferrals can be entered as a percentage of capped compensation or as a dollar amount; a dollar amount overrides the corresponding percentage.</LI>
              <LI>The combined desired deferral is first capped at the annual elective deferral limit.</LI>
              <LI>Any excess above that limit becomes a <Term>catch-up</Term> contribution, capped at the catch-up amount for the employee’s age: the standard amount at 50–59 and 64+, and the enhanced “super catch-up” at 60–63 (SECURE 2.0).</LI>
              <LI>The allowed total is split back into pre-tax and Roth in proportion to what was requested.</LI>
            </List>

            <Sub>6. Employer contributions</Sub>
            <List>
              <LI><Term>Match</Term> is computed tier by tier on the employee’s regular deferral rate (catch-up is excluded from the match base). Each tier matches its rate on the slice of deferral percentage that falls within it. An optional per-employee dollar cap reduces the match proportionally across tiers.</LI>
              <LI><Term>NEC</Term> is its rate times capped compensation.</LI>
              <LI><Term>Profit sharing</Term> compiles the guided formula and funding choice into employee allocations. Pro rata and flat dollar can use direct formulas or compatible budgets; integrated validates the permitted-disparity ladder; points divides a budget using the selected age, service, and compensation weights; age-weighted projects benefits under named assumptions; new comparability applies the authored group rates or solved owner-target rates.</LI>
              <LI>Minimum-hours and last-day conditions apply only to profit sharing and use fixed AND semantics. Employees who miss either adopted condition receive no profit-sharing allocation.</LI>
            </List>

            <Sub>7. 415(c) annual-additions cap</Sub>
            <P>
              Total annual additions (regular employee deferrals + employer + after-tax, excluding catch-up) are
              capped at the 415(c) limit. When regular elective deferrals plus employer contributions alone exceed
              the cap, employer contributions are reduced in a fixed order until the total fits:
              <Term> profit sharing first, then match, then NEC</Term> (so safe-harbor money is reduced last).
              Employee deferrals are never reduced by this cap.
            </P>

            <Sub>8. After-tax contributions (mega-backdoor Roth)</Sub>
            <P>
              After-tax contributions are only modeled when the plan allows them; otherwise any after-tax election
              in the employee list is ignored with a warning. After-tax dollars count toward the 415(c) cap but not the
              elective deferral limit, so they are added <Term>last</Term>, filling the remaining 415(c) headroom
              above elective deferrals and employer contributions.
            </P>
            <List>
              <LI>The requested amount comes from <Term>after_tax_amount</Term>, or <Term>after_tax_percent</Term> of capped compensation when no dollar amount is given.</LI>
              <LI>It is first limited by the plan’s optional after-tax caps (percent of compensation and/or dollar amount; a cap of 0 means no plan-specific limit).</LI>
              <LI>It is then limited to the remaining 415(c) headroom, so after-tax can never push annual additions past the cap. Either reduction is flagged with a warning.</LI>
            </List>
            <Note>
              After-tax contributions are subject to the ACP nondiscrimination test (§401(m)) together with employer
              match, and a safe harbor plan does <Term>not</Term> exempt them. This tool does not run an ACP test;
              concentrated highly-compensated use of after-tax (mega-backdoor Roth) is a common cause of ACP failure
              and corrective refunds. The availability of after-tax contributions is also a benefit, right, or
              feature subject to nondiscriminatory-availability rules (Treas. Reg. §1.401(a)(4)-4).
            </Note>

            <Sub>9. Vesting & forfeitures</Sub>
            <List>
              <LI>Each employer source’s vested percentage is read from its schedule using completed service years. Immediate and safe-harbor sources are 100% vested.</LI>
              <LI>When a schedule depends on service and service is unknown, that amount is reported as <Term>unresolved</Term> – it is not counted as either vested or forfeitable.</LI>
              <LI>For terminated employees, the known <Term>unvested</Term> employer amount becomes an estimated <Term>forfeiture</Term>. Unresolved amounts are reported separately, not forfeited.</LI>
            </List>

            <Sub>10. HCE classification</Sub>
            <P>
              An employee is a Highly Compensated Employee when compensation exceeds the prior-year HCE threshold
              (the lookback rule). When <Term>prior_year_compensation</Term> is provided it is used; otherwise
              current compensation is used as an estimate and the result is flagged as estimated.
            </P>

            <Sub>11. Plan-level metrics</Sub>
            <List>
              <LI>Metrics cover active employees by default, plus terminated employees when that setting is on.</LI>
              <LI><Term>Participation</Term> classifies each person as non-participant, participating, or maxed out (deferrals at the combined regular + catch-up limit).</LI>
              <LI><Term>ADP-style</Term> and <Term>ACP-style</Term> screens apply the statutory permitted-limit rule (the greater of 1.25× the NHCE average, or the lesser of 2× / +2 points) to loaded regular deferrals, match, after-tax contributions, and capped compensation, and estimate a corrective distribution when a screen is over its limit. They do not replace administrator testing.</LI>
              <LI><Term>Coverage (§410(b))</Term> runs the ratio-percentage test (70%) on the non-excludable population and, when the ratio falls short, the <Term>average benefit test</Term> – the nondiscriminatory-classification safe/unsafe percentages plus the average-benefit-percentage prong. Employees who failed plan eligibility, and LTPT deferral-only employees, are treated as excludable. Controlled-group and statutory-exclusion facts are not available.</LI>
              <LI><Term>Cross-testing (§401(a)(4))</Term> models a new-comparability / owner-heavy nonelective or profit-sharing allocation on a benefits basis: each allocation is converted to an equivalent benefit accrual rate (EBAR) at a standard testing age and interest rate, each HCE’s rate group is checked against §410(b), and the design must clear the minimum allocation gateway (the lesser of 5% or one-third of the highest HCE allocation rate). It uses documented interest/mortality assumptions, not the plan’s actual actuarial elections.</LI>
              <LI><Term>Top-heavy</Term> compares key-employee account balances to total balances and screens at 60%, and estimates the non-key minimum contribution and any shortfall. It is only available when every included employee has an explicit key-employee value and balances are present.</LI>
              <LI>Each screen that flags a concern offers a short <Term>“Why / how to address”</Term> explanation with the modeled dollars involved. All compliance cards are planning models – not formal tests or legal conclusions. Testing elections, plan-document definitions, related employers, exclusions, attribution, corrections, distributions, actuarial elections, and administrator adjustments can change the formal result.</LI>
            </List>
          </Section>

          <Section id="limits" title="Contribution limits">
            <H>IRS limits by plan year</H>
            <P>These are the IRS figures applied when you select a plan year. HCE status uses the prior year’s threshold.</P>
            <div className="mt-4 overflow-hidden rounded-lg border border-border">
              <div className="overflow-x-auto">
              {/* One column per selectable plan year, so this outgrows a phone
                  quickly. Plain horizontal scroll: a sticky label column would
                  need an opaque background that matches the translucent card
                  behind it, and any mismatch shows as a seam while panning. */}
              <table className="w-full min-w-[30rem] border-collapse text-left text-sm">
                <thead>
                  <tr className="bg-muted/60 text-xs uppercase tracking-wide text-muted-foreground">
                    <th className="px-3 py-2 font-semibold">Limit</th>
                    {SELECTABLE_PLAN_YEARS.map((year) => (
                      <th key={year} className="px-3 py-2 text-right font-semibold">{year}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {[
                    ['Elective deferral limit', 'deferralLimit'],
                    ['Catch-up (50–59, 64+)', 'catchup50to59'],
                    ['Super catch-up (60–63)', 'superCatchup60to63'],
                    ['415(c) annual additions', 'planLimit415c'],
                    ['401(a)(17) compensation cap', 'compCap401a17'],
                    ['HCE threshold', 'hceThreshold'],
                  ].map(([label, key]) => (
                    <tr key={key} className="border-t border-border">
                      <td className="px-3 py-2 text-xs text-muted-foreground">{label}</td>
                      {SELECTABLE_PLAN_YEARS.map((year) => (
                        <td key={year} className="px-3 py-2 text-right font-mono text-xs text-foreground">
                          {formatCurrency(PLAN_LIMITS[year]?.[key])}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>
            </div>
          </Section>

          <Section id="privacy" title="Data & privacy">
            <H>Where your data lives</H>
            <P>
              The employee list always stays in your browser, calculations run locally, and employee data is never
              uploaded. Exports (PDF, CSV, JSON) are generated on your device. Treat downloaded files as sensitive –
              they can contain compensation and personal data.
            </P>
            <Sub>AI features</Sub>
            <P>
              All AI features are optional and individually consented (see <Term>Julio &amp; AI features</Term>).
              What each may send: Julio – sanitized aggregates and redacted findings; plan-document import – the
              document&rsquo;s text; the estimator assist – the company description you typed; the Studio&rsquo;s
              column matching – column headers and masked value shapes. Employee rows, names, IDs, and amounts never
              leave the browser, AI traffic is encrypted, and a Zero Data Retention policy applies – nothing is
              stored or used for training.
            </P>
            <P>
              Results are estimates for planning and illustration only and are not a compliance test, actuarial
              valuation, or tax advice.
            </P>
          </Section>
        </div>
      </div>
    </div>
  )
}
